import { useState, useCallback } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * Tome page data from the backend.
 */
interface TomePage {
    id: number;
    name: string;
    slug: string;
    order: number;
    is_published: boolean;
    media_count: number;
    chapter_id: number | null;
    chapter_title: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * Chapter data for filtering.
 */
interface TomeChapter {
    id: number;
    title: string;
    pages_count: number;
}

/**
 * Book data from the backend.
 */
interface TomeBook {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    hue: number;
    order: number;
    is_published: boolean;
    cover_image_url: string | null;
}

/**
 * Pagination link from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated tome pages response.
 */
interface PaginatedTomePages {
    data: TomePage[];
    links: PaginationLink[];
    last_page: number;
}

/**
 * Props for the Book Show page.
 */
interface Props {
    book: TomeBook;
    chapters: TomeChapter[];
    pages: PaginatedTomePages;
    filters: {
        search: string | null;
        published: string | null;
        chapter: string | null;
    };
}

/**
 * GM Book Show page - displays a book's details and all its pages organized by chapter.
 */
export default function Show({ book, chapters, pages, filters }: Props) {
    const [search, setSearch] = useState(filters.search || '');
    const [publishedFilter, setPublishedFilter] = useState(filters.published || '');
    const [chapterFilter, setChapterFilter] = useState(filters.chapter || '');

    // Generate accent color from book hue
    const accentColor = `hsl(${book.hue}, 50%, 40%)`;

    // Handle search/filter submission
    const handleFilter = useCallback(() => {
        router.get(`/gm/tome/books/${book.id}`, {
            search: search || undefined,
            published: publishedFilter || undefined,
            chapter: chapterFilter || undefined,
        }, { preserveState: true });
    }, [book.id, search, publishedFilter, chapterFilter]);

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setPublishedFilter('');
        setChapterFilter('');
        router.get(`/gm/tome/books/${book.id}`, {}, { preserveState: true });
    };

    // Handle delete
    const handleDelete = (page: TomePage) => {
        if (confirm(`Are you sure you want to delete "${page.name}"?`)) {
            router.delete(`/gm/tome/${page.id}`, { preserveScroll: true });
        }
    };

    return (
        <GmLayout title={`Book: ${book.title}`} activePath="/gm/tome/books">
            <div className="max-w-7xl">
                {/* Breadcrumb and Header */}
                <div className="mb-6">
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                        <Link href="/gm/tome/books" className="hover:text-white">📚 Books</Link>
                        <span>→</span>
                        <span style={{ color: accentColor }}>{book.title}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <h1 className="text-2xl font-bold text-white">📖 {book.title}</h1>
                            <span className={`px-2 py-1 text-xs rounded ${book.is_published ? 'bg-green-600' : 'bg-yellow-600'}`}>
                                {book.is_published ? 'Published' : 'Draft'}
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <Link
                                href={`/gm/tome/books/${book.id}/edit`}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Edit Book
                            </Link>
                            <Link
                                href={`/gm/tome/create?book=${book.id}`}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                + Add Page
                            </Link>
                        </div>
                    </div>
                    {book.description && (
                        <p className="text-gray-400 mt-2">{book.description}</p>
                    )}
                </div>

                {/* Chapter Summary */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Chapters</h2>
                    <div className="flex flex-wrap gap-2">
                        {chapters.map((chapter) => (
                            <Link
                                key={chapter.id}
                                href={`/gm/tome/books/${book.id}/chapters/${chapter.id}/edit`}
                                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded transition-colors"
                            >
                                {chapter.title} <span className="text-gray-400">({chapter.pages_count})</span>
                            </Link>
                        ))}
                        <Link
                            href={`/gm/tome/books/${book.id}/chapters/create`}
                            className="px-3 py-1.5 bg-purple-600/50 hover:bg-purple-600 text-white text-sm rounded transition-colors"
                        >
                            + Add Chapter
                        </Link>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm text-gray-400 mb-1">Search</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                                placeholder="Page name or content"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                        </div>

                        {/* Chapter Filter */}
                        <div className="w-48">
                            <label className="block text-sm text-gray-400 mb-1">Chapter</label>
                            <select
                                value={chapterFilter}
                                onChange={(e) => setChapterFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All Chapters</option>
                                {chapters.map((chapter) => (
                                    <option key={chapter.id} value={chapter.id}>{chapter.title}</option>
                                ))}
                            </select>
                        </div>

                        {/* Published Filter */}
                        <div className="w-40">
                            <label className="block text-sm text-gray-400 mb-1">Status</label>
                            <select
                                value={publishedFilter}
                                onChange={(e) => setPublishedFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All</option>
                                <option value="true">Published</option>
                                <option value="false">Draft</option>
                            </select>
                        </div>

                        {/* Filter Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleFilter}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                Apply
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Pages Table */}
                {pages.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No pages found in this book. Add a page to start writing content.
                    </div>
                ) : (
                    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Order</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Chapter</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Images</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Author</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {pages.data.map((page) => (
                                        <TomePageRow key={page.id} page={page} onDelete={handleDelete} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {pages.last_page > 1 && (
                    <Pagination links={pages.links} />
                )}
            </div>
        </GmLayout>
    );
}

/**
 * TomePageRow - Displays a single tome page row with actions.
 */
function TomePageRow({ page, onDelete }: { page: TomePage; onDelete: (page: TomePage) => void }) {
    return (
        <tr>
            {/* Order */}
            <td className="px-4 py-3 text-sm text-gray-400">
                {page.order}
            </td>

            {/* Name */}
            <td className="px-4 py-3">
                <div>
                    <Link href={`/gm/tome/${page.id}/edit`} className="text-white font-medium hover:text-purple-400">
                        {page.name}
                    </Link>
                    <p className="text-xs text-gray-500">/tome/{page.slug}</p>
                </div>
            </td>

            {/* Chapter */}
            <td className="px-4 py-3 text-sm text-gray-400">
                {page.chapter_title || <span className="text-gray-600">—</span>}
            </td>

            {/* Status */}
            <td className="px-4 py-3">
                <span className={`px-2 py-1 text-xs rounded ${page.is_published ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'}`}>
                    {page.is_published ? 'Published' : 'Draft'}
                </span>
            </td>

            {/* Images */}
            <td className="px-4 py-3 text-sm text-gray-400">
                {page.media_count}
            </td>

            {/* Author */}
            <td className="px-4 py-3 text-sm text-gray-400">
                {page.created_by || 'Unknown'}
            </td>

            {/* Actions */}
            <td className="px-4 py-3">
                <div className="flex gap-2">
                    <Link
                        href={`/gm/tome/${page.id}/edit`}
                        className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                        Edit
                    </Link>
                    <a
                        href={`/tome/page/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 text-xs bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                    >
                        View
                    </a>
                    <button
                        onClick={() => onDelete(page)}
                        className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                        Delete
                    </button>
                </div>
            </td>
        </tr>
    );
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}
